import { useTranslations } from '@vocab/react';
import {
  Box,
  ButtonLink,
  Column,
  Columns,
  Heading,
  IconNewWindow,
  Stack,
  Text,
} from 'braid-design-system';
import { useEffect } from 'react';

import { useAnalytics } from 'src/modules/analytics/AnalyticsProvider';
import { useSeekPassUrl } from 'src/modules/hooks/useSeekPassUrl';

import { PoweredBySeekPassFooter } from '../PoweredBySeekPassFooter';
import { verifiableCredentialTypenameToCertsyType } from '../analyticsUtils';
import { useVerifications } from '../data';
import type { VerifiableCredential } from '../types';
import { useGetLicenceExpiryStatusBadge } from '../useGetLicenceExpiryStatusBadge/useGetLicenceExpiryStatusBadge';

import translations from './.vocab';
import { TrophySvg } from './TrophySvg';

const VerifiableCredentialItem = ({
  credential,
}: {
  credential: VerifiableCredential;
}) => {
  const { t } = useTranslations(translations);

  const { label, url, __typename } = credential;

  const { seekPassVerifyClicked } = useAnalytics();
  const trackingEntityName =
    'trackingEntityName' in credential && credential.trackingEntityName !== null
      ? credential.trackingEntityName
      : undefined;

  const expiryStatus =
    'expiryStatus' in credential ? credential.expiryStatus : null;
  const isVerifiedCredential =
    expiryStatus === 'EXPIRED' || expiryStatus === 'EXPIRING_SOON';
  const badge = useGetLicenceExpiryStatusBadge(expiryStatus);

  const verifyButtonOnClick = () => {
    const certsyType = verifiableCredentialTypenameToCertsyType[__typename];
    seekPassVerifyClicked({
      certsyType,
      entityName: trackingEntityName,
      expiryStatus: expiryStatus ?? undefined,
    });
  };

  return (
    <>
      <Columns space="medium" alignY="center">
        <Column>
          <Text>
            <Box component="span" paddingRight={badge ? 'xsmall' : undefined}>
              {label}
            </Box>
            {badge}
          </Text>
        </Column>
        <Column width="content">
          <ButtonLink
            href={url}
            tone="formAccent"
            variant="transparent"
            onClick={verifyButtonOnClick}
            target="_blank"
            bleed
            data={{
              automation: `verify-credential-${credential.__typename}`,
            }}
            icon={<IconNewWindow />}
            iconPosition="trailing"
          >
            {isVerifiedCredential ? t('Re-verify') : t('Verify')}
          </ButtonLink>
        </Column>
      </Columns>
    </>
  );
};

export const AvailableVerifications = () => {
  const { t } = useTranslations(translations);

  const { verificationsDrawerOpened, seekPassInReviewClicked } = useAnalytics();

  const { verifiable, verificationsTrackingProps } = useVerifications();

  const seekPassUrl = useSeekPassUrl(
    'https://app.seekpass.co/career-passport/recommended',
  );

  useEffect(() => {
    const { credentialsNotVerifiedTotal, credentialsNotVerifiedType } =
      verificationsTrackingProps;

    verificationsDrawerOpened({
      credentialsNotVerifiedTotal,
      credentialsNotVerifiedType,
      currentPageSubsection: 'available verifications',
    });
    // Only send tracking once on initial render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emptyContent = (
    <Stack space="medium" align="center">
      <TrophySvg />
      <Heading level="4" align="center">
        {t('Great work, all available information verified!')}
      </Heading>
      <Text tone="secondary">
        {t('We will let you know when there are more verifications available.')}
      </Text>
    </Stack>
  );

  const pendingStatus = (count: number) => (
    <Columns space="medium" alignY="center">
      <Column>
        <Text>
          {t('In review')} ({count})
        </Text>
      </Column>
      <Column width="content">
        <ButtonLink
          tone="formAccent"
          variant="transparent"
          onClick={() => seekPassInReviewClicked()}
          href={seekPassUrl}
          target="_blank"
          bleed
          icon={<IconNewWindow />}
          iconPosition="trailing"
        >
          {t('Go to SEEK Pass')}
        </ButtonLink>
      </Column>
    </Columns>
  );

  return (
    <Stack space="none">
      {verifiable && verifiable.length > 0 ? (
        <Stack space="xlarge">
          {verifiable.map(
            ({ label, credentials, pendingCount }, sectionIndex) => (
              <Stack space="large" key={sectionIndex}>
                <Text weight="strong">{label}</Text>
                <Stack space="xlarge">
                  {credentials
                    ? credentials.map((credential, credentialIndex) => (
                        <VerifiableCredentialItem
                          key={credentialIndex}
                          credential={credential}
                        />
                      ))
                    : null}
                  {pendingCount ? pendingStatus(pendingCount) : null}
                </Stack>
              </Stack>
            ),
          )}
        </Stack>
      ) : (
        emptyContent
      )}
      <Box paddingBottom="xlarge" />
      <PoweredBySeekPassFooter />
    </Stack>
  );
};
